import * as R from 'ramda';
import { connect } from 'react-redux';
import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { pure, compose, withState, withHandlers } from 'react-recompose';
// components
import { SelectMui } from '../../../components/select-mui';
import { PageReportFilters } from '../../../components/filter/report-and-quick-filters';
// features
import { makeSelectCurrentUserName } from '../../auth/selectors';
import { makeSelectAvailableReportGeoFencingZoneList } from '../../geo-fencing-zone/report/selectors';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
// hocs
import { withAsyncPinReport } from '../../../hocs';
// ui
import { Box, ListWrapper, RelativeFlex } from '../../../ui';
// feature pivot-table
import PivotTable from '../';
import ReportSelector from './report-selector';
import { PinnedReports } from './pinned-reports';
import { filterPropsMap } from '../settings/filter-params';
import { themeOptions, defaultThemes } from '../settings/theme';
import { makeSelectUsedReport, makeSelectAvailableReports } from '../selectors';
import {
  setReports,
  setUsedReport,
  updateReportRequest,
  getPivotTableDataRequest,
  changeDefaultReportRequest,
} from '../actions';
//////////////////////////////////////////////////

const PivotTableWrapper = (props) => {
  const {
    p,
    pl,
    tabs,
    theme,
    reportList,
    selectedReport,
    handleChangeTheme,
    listWrapperStyles,
    hideReportFilters,
    relativeFlexStyles,
    handleSelectReport,
    handlePinOrUnpinReport,
    getPivotTableDataRequest,
    changeDefaultReportRequest,
  } = props;

  const pinnedReports = R.compose(
    R.sortBy(R.prop(GC.FIELD_PINNED_REPORT_INDEX)),
    R.filter(R.prop('pinnedReport')),
  )(R.or(reportList, []));

  const filterProps = G.getPropFromObject(G.getPropFromObject(GC.FIELD_TYPE, selectedReport), filterPropsMap);

  useEffect(() => {
    if (G.isNilOrEmpty(selectedReport)) return;

    const pivotSettings = JSON.parse(R.pathOr('""', ['pivotSettings'], selectedReport));
    const reportTheme = R.pathOr('softdefault', ['theme'], pivotSettings);

    if (R.equals(theme, reportTheme)) return;

    handleChangeTheme(reportTheme);
  }, [selectedReport]);

  return (
    <ListWrapper
      pl={R.or(pl, 65)}
      p={R.or(p, 15)}
      bgColor={G.getTheme('pages.layOutBgColor')}
      {...G.spreadUiStyles(listWrapperStyles)}
    >
      <RelativeFlex zIndex={12} alignItems='flex-end' {...G.spreadUiStyles(relativeFlexStyles)}>
        {tabs}
        <SelectMui
          multiple={false}
          isClearable={false}
          initialValue={theme}
          options={themeOptions}
          wrapperStyles={{ ml: 15, width: 160 }}
          handleChangeCallback={handleChangeTheme}
          label={G.getWindowLocale('titles:select-theme', 'Select Theme')}
        />
        <Box ml='auto' zIndex={11}>
          <ReportSelector {...props} filterProps={filterProps} />
        </Box>
      </RelativeFlex>
      {
        G.isNotEmpty(pinnedReports) &&
        <PinnedReports
          list={pinnedReports}
          selectedReport={selectedReport}
          handleSelectReport={handleSelectReport}
          handlePinOrUnpinReport={handlePinOrUnpinReport}
          changeDefaultReportRequest={changeDefaultReportRequest}
        />
      }
      {
        R.and(R.not(hideReportFilters), G.isNotNilAndNotEmpty(selectedReport)) &&
        <PageReportFilters
          {...props}
          filterProps={filterProps}
          usePortalForFilters={true}
          getItemListRequest={getPivotTableDataRequest}
        />
      }
      <PivotTable {...props} />
    </ListWrapper>
  );
};

const mapStateToProps = (state) => createStructuredSelector({
  selectedReport: makeSelectUsedReport(state),
  reportList: makeSelectAvailableReports(state),
  userLoginId: makeSelectCurrentUserName(state),
  geoFencingZones: makeSelectAvailableReportGeoFencingZoneList(state),
});

const enhance = compose(
  connect(mapStateToProps, {
    setReports,
    setUsedReport,
    updateReportRequest,
    getPivotTableDataRequest,
    changeDefaultReportRequest,
  }),
  withState('theme', 'setTheme', 'softdefault'),
  withHandlers({
    handleChangeTheme: ({ theme, setTheme }) => (newTheme) => {
      if (R.or(R.equals(theme, newTheme), G.notContain(newTheme, defaultThemes))) return;

      const link = document.getElementById('flexmonster-theme');

      if (link) {
        link.href = `https://cdn.flexmonster.com/theme/${newTheme}/flexmonster.min.css`;
      }

      setTheme(newTheme);
    },
    handleSelectReport: ({ reportList, setUsedReport, getPivotTableDataRequest }) => (reportGuid) => {
      const selectedReport = R.find(R.propEq(reportGuid, GC.FIELD_GUID), reportList);
      setUsedReport(selectedReport);
      getPivotTableDataRequest();
    },
  }),
  withAsyncPinReport,
  pure,
);

export default enhance(PivotTableWrapper);
